import axios, { type AxiosRequestConfig, type AxiosError } from 'axios';
import type { BaseQueryFn } from '@reduxjs/toolkit/query';
import { toast } from 'sonner';

const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

axiosInstance.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (
      error.response?.status === 401 &&
      !window.location.pathname.includes('login') &&
      !window.location.pathname.includes('external-auditor') &&
      !window.location.pathname.includes('role-invitation')
    ) {
      window.location.href = `/login`;
    }
    return Promise.reject(error);
  }
);

// Extracts the human-readable message from a backend error payload, i.e. the
// `data` field of `ResponseWrapper` (`{ message, data, statusCode }`). Accepts
// the response payload directly (not the raw error) so callers that need to
// decompress a Blob payload first can pass the decompressed value.
export const getAxiosErrorMessage = (
  payload: { data?: unknown; message?: string } | undefined,
  fallback = 'Something went wrong. Please try again.'
): string =>
  typeof payload?.data === 'string'
    ? payload.data
    : ((payload?.data as { message?: string } | undefined)?.message ??
      payload?.message ??
      fallback);

export type AxiosBaseQueryArgs = {
  url: string;
  method?: AxiosRequestConfig['method'];
  data?: AxiosRequestConfig['data'];
  params?: AxiosRequestConfig['params'];
  headers?: AxiosRequestConfig['headers'];
  responseType?: AxiosRequestConfig['responseType'];
};

export const axiosBaseQuery: BaseQueryFn<AxiosBaseQueryArgs, unknown, unknown> = async (
  { url, method = 'GET', data, params, headers, responseType },
  api
) => {
  try {
    const result = await axiosInstance({
      url,
      method,
      data,
      params,
      headers,
      responseType,
      signal: api.signal,
    });
    return { data: result.data };
  } catch (error) {
    const axiosError = error as AxiosError<{ data?: unknown; message?: string }>;
    const status = axiosError.response?.status;
    let payload = axiosError.response?.data;

    // Decompress Blob payloads. This occurs when an endpoint configured with
    // `responseType: 'blob'` returns a 4xx/5xx JSON error instead of a file.
    if (payload instanceof Blob) {
      try {
        payload = JSON.parse(await payload.text());
      } catch {
        /* Fall back to original payload if it's not valid JSON */
      }
    }

    const message = getAxiosErrorMessage(payload);
    if (status === 403) {
      toast.error(message);
    }

    return {
      error: {
        status,
        data: payload ?? axiosError.message,
      },
    };
  }
};

export default axiosInstance;
